import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Plus, ArrowRight, ArrowLeft, CheckCircle, CreditCard, BanknoteIcon, Landmark, CheckSquare, RefreshCw, DollarSign, Calendar, FileText, Tag } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { useAppStore } from '../../store';
import { Expense } from '../../types';
import { formatCurrency } from '../../utils/formatters';
import { getCurrentArgentinaISOString } from '../../utils/dateUtils';

// Helper functions defined at module level
const getPaymentMethodName = (method: string) => {
  const methods = {
    cash: 'Efectivo',
    transfer: 'Transferencia',
    card: 'Tarjeta',
    check: 'Cheque',
    mercadopago: 'MercadoPago'
  };
  return methods[method as keyof typeof methods] || method;
};

const getRecurrenceName = (recurrence?: string) => {
  if (!recurrence) return '';
  const recurrences = {
    daily: 'Diaria',
    weekly: 'Semanal',
    monthly: 'Mensual',
    quarterly: 'Trimestral',
    yearly: 'Anual'
  };
  return recurrences[recurrence as keyof typeof recurrences] || recurrence;
};

const formatArgentinaDate = (dateString: string) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-AR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const getCategoryName = (categoryId: string, categories: any[]) => {
  if (!categoryId) return '';
  const category = categories.find(cat => cat.id === categoryId);
  return category ? category.name : '';
};

interface ExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (expense: Omit<Expense, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  initialData?: Expense;
}

// Define the steps of the wizard
type WizardStep = 1 | 2 | 3;

const ExpenseModal: React.FC<ExpenseModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
}) => {
  const { expenseCategories, fetchExpenseCategories } = useAppStore();
  
  const [formData, setFormData] = useState<Omit<Expense, 'id' | 'created_at' | 'updated_at'>>({
    supplier_id: undefined,
    category_id: '',
    description: '',
    amount: 0,
    vat: 0,
    payment_method: 'cash',
    date: getCurrentArgentinaISOString(),
    is_recurring: false,
    recurrence: undefined,
  });
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [stepValidation, setStepValidation] = useState({
    step1Valid: false,
    step2Valid: false,
    step3Valid: true, // Step 3 is always valid as it's just a confirmation
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  
  // Estado para manejar el ajuste automático de fecha
  const [dateAdjustment, setDateAdjustment] = useState<{
    originalDate: string;
    adjustedDate: string;
    showConfirmation: boolean;
  } | null>(null);

  // Fetch expense categories
  useEffect(() => {
    if (isOpen) {
      fetchExpenseCategories();
      // Reset to step 1 when modal opens
      setCurrentStep(1);
    }
  }, [isOpen, fetchExpenseCategories]);
  
  const getProgress = () => {
    return (currentStep / 3) * 100;
  };
  
  // Validate each step
  useEffect(() => {
    // Step 1: Category selection
    const step1Valid = !!formData.category_id;
    
    // Step 2: Description and amount
    const step2Valid = !!formData.description.trim() && formData.amount > 0;
    
    // Step 3 is always valid (confirmation step)
    
    setStepValidation({
      step1Valid,
      step2Valid,
      step3Valid: true
    });
  }, [formData]);
  
  useEffect(() => {
    if (initialData) {
      setFormData({
        supplier_id: initialData.supplier_id,
        category_id: initialData.category_id,
        description: initialData.description,
        amount: initialData.amount,
        vat: initialData.vat || 0,
        payment_method: initialData.payment_method,
        date: initialData.date,
        is_recurring: initialData.is_recurring,
        recurrence: initialData.recurrence,
        parent_id: initialData.parent_id,
      });
    } else {
      setFormData({
        supplier_id: undefined,
        category_id: '',
        description: '',
        vat: 0,
        amount: 0,
        payment_method: 'cash',
        date: getCurrentArgentinaISOString(),
        is_recurring: false,
        recurrence: undefined,
      });
    }
    setError(null);
  }, [initialData, isOpen]);
  
  // Navigation functions
  const goToNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(prev => (prev + 1) as WizardStep);
    }
  };
  
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => (prev - 1) as WizardStep);
    }
  };
  
  // Check if current step is valid
  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 1: return stepValidation.step1Valid;
      case 2: return stepValidation.step2Valid;
      case 3: return stepValidation.step3Valid;
      default: return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.category_id) {
      setError('La categoría es obligatoria');
      return;
    }
    
    if (!formData.description.trim()) {
      setError('La descripción es obligatoria');
      return;
    }
    
    if (!formData.amount || formData.amount <= 0) {
      setError('El monto debe ser mayor a 0');
      return;
    }
    
    if (formData.is_recurring && !formData.recurrence) {
      setError('Debe seleccionar una recurrencia');
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Ajustar automáticamente la fecha si es el día 1 del mes
      let adjustedFormData = { ...formData };
      const selectedDate = new Date(formData.date);
      const day = selectedDate.getDate();
      
      if (day === 1) {
        // Si es el día 1, sumar un día para que aparezca en el mes correcto
        const adjustedDate = new Date(selectedDate);
        adjustedDate.setDate(selectedDate.getDate() + 1);
        
        adjustedFormData = {
          ...formData,
          date: adjustedDate.toISOString().split('T')[0]
        };
        
        console.log('📅 Fecha ajustada automáticamente:', {
          fechaOriginal: formData.date,
          fechaAjustada: adjustedFormData.date,
          motivo: 'Día 1 del mes - ajustado al día 2 para evitar problemas de filtrado'
        });
        
        // Mostrar confirmación al usuario sobre el ajuste de fecha
        setDateAdjustment({
          originalDate: formData.date,
          adjustedDate: adjustedFormData.date,
          showConfirmation: true
        });
        return; // No continuar, mostrar la confirmación al usuario
      }
      
      await onSave(adjustedFormData);
      onClose();
    } catch (error: any) {
      console.error('Error saving expense:', error);
      setError(error.message || 'Error al guardar el gasto');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Función para confirmar el ajuste de fecha
  const handleConfirmDateAdjustment = async () => {
    if (!dateAdjustment) return;
    
    try {
      setIsLoading(true);
      
      // Crear el gasto con la fecha ajustada
      const adjustedFormData = {
        ...formData,
        date: dateAdjustment.adjustedDate
      };
      
      await onSave(adjustedFormData);
      onClose();
    } catch (error: any) {
      console.error('Error saving expense:', error);
      setError(error.message || 'Error al guardar el gasto');
    } finally {
      setIsLoading(false);
      setDateAdjustment(null);
    }
  };

  // Función para cancelar el ajuste de fecha
  const handleCancelDateAdjustment = () => {
    setDateAdjustment(null);
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      setError('El nombre de la categoría no puede estar vacío');
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Insert new category
      const { supabase } = await import('../../lib/supabase');
      const { data, error } = await supabase
        .from('expenses_categories')
        .insert([{ name: newCategoryName.trim() }])
        .select()
        .single();
      
      if (error) {
        if (error.code === '23505') { // Unique violation
          throw new Error('Ya existe una categoría con ese nombre');
        }
        throw error;
      }
      
      // Update categories list
      // We need to refresh the categories from the store
      await fetchExpenseCategories();
      
      // Select the new category
      setFormData(prev => ({
        ...prev,
        category_id: data.id
      }));
      
      // Reset state
      setNewCategoryName('');
      setIsCategoryModalOpen(false);
    } catch (error: any) {
      console.error('Error adding category:', error);
      setError(error.message || 'Error al crear la categoría');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Format amount as currency
  const formatAmountAsCurrency = (value: number) => {
    if (isNaN(value)) return '';
    return formatCurrency(value);
  };
  
  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="bg-indigo-50 dark:bg-indigo-900/30 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-medium text-indigo-800 dark:text-indigo-200 flex items-center">
                <Tag className="mr-2 h-5 w-5" />
                Paso 1: Categoría
              </h3>
              <p className="text-sm text-indigo-600 dark:text-indigo-300 mt-1">
                Selecciona la categoría del gasto.
              </p>
            </div>
            
            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Categoría
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Select
                    label=""
                    value={formData.category_id}
                    onChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}
                    options={[
                      { value: '', label: 'Seleccionar categoría...' },
                      ...expenseCategories.map(category => ({
                        value: category.id,
                        label: category.name,
                      }))
                    ]}
                    required
                  />
                </div>
                <div className="flex items-center gap-2">
                  {isCategoryModalOpen ? (
                    <>
                      <input
                        type="text"
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="Nueva categoría"
                        className="h-12 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddCategory}
                        disabled={isLoading || !newCategoryName.trim()}
                        className="h-12"
                      >
                        Guardar
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setIsCategoryModalOpen(false);
                          setNewCategoryName('');
                        }}
                        className="h-12"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsCategoryModalOpen(true)}
                      icon={<Plus className="h-4 w-4" />}
                      className="h-12 flex items-center justify-center"
                    >
                      Nueva
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
        
      case 2:
        return (
          <div className="space-y-6">
            <div className="bg-indigo-50 dark:bg-indigo-900/30 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-medium text-indigo-800 dark:text-indigo-200 flex items-center">
                <FileText className="mr-2 h-5 w-5" />
                Paso 2: Detalles del Gasto
              </h3>
              <p className="text-sm text-indigo-600 dark:text-indigo-300 mt-1">
                Describe el gasto y especifica el monto.
              </p>
            </div>
            
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Descripción
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                rows={3}
                placeholder="Describe el gasto..."
                required
              />
            </div>
            
            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Monto
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                  className="w-full h-12 pl-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                  min="0.01"
                  step="0.01"
                  required
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-500 dark:text-gray-400">
                  $
                </div>
              </div>
              {formData.amount > 0 && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {formatAmountAsCurrency(formData.amount)}
                </p>
              )}
            </div>
            
            {/* VAT Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                IVA
              </label>
              <Select
                label=""
                value={formData.vat.toString()}
                onChange={(value) => setFormData(prev => ({ 
                  ...prev, 
                  vat: parseFloat(value),
                }))}
                options={[
                  { value: '0', label: 'No Gravado (0%)' },
                  { value: '10.5', label: 'IVA 10.5%' },
                  { value: '21', label: 'IVA 21%' },
                ]}
              />
              {formData.vat > 0 && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Monto de IVA: {formatCurrency(formData.amount * (formData.vat / 100))}
                </p>
              )}
            </div>
          </div>
        );
        
      case 3:
        return (
          <div className="space-y-6">
            <div className="bg-indigo-50 dark:bg-indigo-900/30 p-4 rounded-lg mb-6">
              <h3 className="text-lg font-medium text-indigo-800 dark:text-indigo-200 flex items-center">
                <Calendar className="mr-2 h-5 w-5" />
                Paso 3: Método de Pago, Fecha y Recurrencia
              </h3>
              <p className="text-sm text-indigo-600 dark:text-indigo-300 mt-1">
                Completa los detalles finales del gasto.
              </p>
            </div>
            
            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Método de Pago
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, payment_method: 'cash' }))}
                  className={`flex flex-col items-center justify-center p-4 rounded-lg border ${
                    formData.payment_method === 'cash'
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <BanknoteIcon className="h-6 w-6 mb-2" />
                  <span className="text-sm">Efectivo</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, payment_method: 'transfer' }))}
                  className={`flex flex-col items-center justify-center p-4 rounded-lg border ${
                    formData.payment_method === 'transfer'
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <Landmark className="h-6 w-6 mb-2" />
                  <span className="text-sm">Transferencia</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, payment_method: 'card' }))}
                  className={`flex flex-col items-center justify-center p-4 rounded-lg border ${
                    formData.payment_method === 'card'
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <CreditCard className="h-6 w-6 mb-2" />
                  <span className="text-sm">Tarjeta</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, payment_method: 'check' }))}
                  className={`flex flex-col items-center justify-center p-4 rounded-lg border ${
                    formData.payment_method === 'check'
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <CheckSquare className="h-6 w-6 mb-2" />
                  <span className="text-sm">Cheque</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, payment_method: 'mercadopago' }))}
                  className={`flex flex-col items-center justify-center p-4 rounded-lg border ${
                    formData.payment_method === 'mercadopago'
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <DollarSign className="h-6 w-6 mb-2" />
                  <span className="text-sm">MercadoPago</span>
                </button>
              </div>
            </div>
            
            {/* Date */}
            <Input
              type="date"
              label="Fecha"
              id="date"
              name="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              required
            />

            {/* Is Recurring */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_recurring"
                checked={formData.is_recurring}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  is_recurring: e.target.checked,
                  // Clear recurrence if unchecked
                  recurrence: e.target.checked ? prev.recurrence : undefined
                }))}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="is_recurring" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                Es un gasto recurrente
              </label>
            </div>
            
            {/* Recurrence (only if is_recurring is true) */}
            {formData.is_recurring && (
              <Select
                label="Recurrencia"
                value={formData.recurrence || ''}
                onChange={(value) => setFormData(prev => ({ ...prev, recurrence: value as Expense['recurrence'] }))}
                options={[
                  { value: '', label: 'Seleccionar recurrencia...' },
                  { value: 'daily', label: 'Diaria' },
                  { value: 'weekly', label: 'Semanal' },
                  { value: 'monthly', label: 'Mensual' },
                  { value: 'quarterly', label: 'Trimestral' },
                  { value: 'yearly', label: 'Anual' },
                ]}
                required
              />
            )}
            
            {/* Summary */}
            <div className="mt-6 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">Resumen del Gasto</h4>
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Categoría:</dt>
                  <dd className="text-sm font-medium text-gray-900 dark:text-white">
                    {getCategoryName(formData.category_id, expenseCategories)}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Descripción:</dt>
                  <dd className="text-sm font-medium text-gray-900 dark:text-white">{formData.description}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500 dark:text-gray-400">IVA ({formData.vat}%):</dt>
                  <dd className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatCurrency(formData.amount * (formData.vat / 100))}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Monto total con IVA:</dt>
                  <dd className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(formData.amount * 1)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Método de Pago:</dt>
                  <dd className="text-sm font-medium text-gray-900 dark:text-white">{getPaymentMethodName(formData.payment_method)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Fecha:</dt>
                  <dd className="text-sm font-medium text-gray-900 dark:text-white">{formatArgentinaDate(formData.date)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-500 dark:text-gray-400">Recurrente:</dt>
                  <dd className="text-sm font-medium text-gray-900 dark:text-white">
                    {formData.is_recurring ? `Sí (${getRecurrenceName(formData.recurrence)})` : 'No'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40 transition-opacity" />
          <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[600px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg z-50 overflow-y-auto transition-all">
            <Dialog.Title className="text-xl font-semibold mb-2 text-gray-900 dark:text-white flex items-center">
              {initialData ? 'Editar' : 'Nuevo'} Gasto {!initialData && <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">Paso {currentStep} de 3</span>}
            </Dialog.Title>
            
            {!initialData && (
              <div className="mb-6">
                {/* Progress bar */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-1">
                  <div 
                    className="bg-indigo-600 dark:bg-indigo-500 h-2.5 rounded-full transition-all duration-300 ease-in-out" 
                    style={{ width: `${getProgress()}%` }}
                  ></div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              {initialData ? (
                // For editing, show the original form
                <div className="space-y-4">
                  {/* Category Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Categoría
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Select
                          label=""
                          value={formData.category_id}
                          onChange={(value) => setFormData(prev => ({ ...prev, category_id: value }))}
                          options={[
                            { value: '', label: 'Seleccionar categoría...' },
                            ...expenseCategories.map(category => ({
                              value: category.id,
                              label: category.name,
                            }))
                          ]}
                          required
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        {isCategoryModalOpen ? (
                          <>
                            <input
                              type="text"
                              value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              placeholder="Nueva categoría"
                              className="h-12 px-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={handleAddCategory}
                              disabled={isLoading || !newCategoryName.trim()}
                              className="h-12"
                            >
                              Guardar
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setIsCategoryModalOpen(false);
                                setNewCategoryName('');
                              }}
                              className="h-12"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setIsCategoryModalOpen(true)}
                            icon={<Plus className="h-4 w-4" />}
                            className="h-12 flex items-center justify-center"
                          >
                            Nueva
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Descripción
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                      rows={3}
                      placeholder="Agregar descripción del gasto..."
                      required
                    />
                  </div>
                  
                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Monto
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        value={formData.amount}
                        onChange={(e) => setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                        className="w-full h-12 pl-3 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                        min="0.01"
                        step="0.01"
                        required
                      />
                    </div>
                    {formData.amount > 0 && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {formatAmountAsCurrency(formData.amount)}
                      </p>
                    )}
                  </div>
                  
                  {/* VAT Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      IVA
                    </label>
                    <Select
                      label=""
                      value={formData.vat.toString()}
                      onChange={(value) => setFormData(prev => ({ 
                        ...prev, 
                        vat: parseFloat(value),
                      }))}
                      options={[
                        { value: '0', label: 'No Gravado (0%)' },
                        { value: '10.5', label: 'IVA 10.5%' },
                        { value: '21', label: 'IVA 21%' },
                      ]}
                    />
                    {formData.vat > 0 && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Monto de IVA: {formatCurrency(formData.amount * (formData.vat / 100))}
                      </p>
                    )}
                  </div>
                  
                  {/* Payment Method */}
                  <Select
                    label="Método de Pago"
                    value={formData.payment_method}
                    onChange={(value) => setFormData(prev => ({ ...prev, payment_method: value as Expense['payment_method'] }))}
                    options={[
                      { value: 'cash', label: 'Efectivo' },
                      { value: 'transfer', label: 'Transferencia' },
                      { value: 'card', label: 'Tarjeta' },
                      { value: 'check', label: 'Cheque' },
                      { value: 'mercadopago', label: 'MercadoPago' },
                    ]}
                    required
                  />
                  
                  {/* Date */}
                  <Input
                    type="date"
                    label="Fecha"
                    id="date"
                    name="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    required
                  />
                  
                  {/* Is Recurring */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_recurring"
                      checked={formData.is_recurring}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        is_recurring: e.target.checked,
                        // Clear recurrence if unchecked
                        recurrence: e.target.checked ? prev.recurrence : undefined
                      }))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_recurring" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                      Es un gasto recurrente
                    </label>
                  </div>
                  
                  {/* Recurrence (only if is_recurring is true) */}
                  {formData.is_recurring && (
                    <Select
                      label="Recurrencia"
                      value={formData.recurrence || ''}
                      onChange={(value) => setFormData(prev => ({ ...prev, recurrence: value as Expense['recurrence'] }))}
                      options={[
                        { value: '', label: 'Seleccionar recurrencia...' },
                        { value: 'daily', label: 'Diaria' },
                        { value: 'weekly', label: 'Semanal' },
                        { value: 'monthly', label: 'Mensual' },
                        { value: 'quarterly', label: 'Trimestral' },
                        { value: 'yearly', label: 'Anual' },
                      ]}
                      required
                    />
                  )}
                  
                  {error && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md text-sm">
                      {error}
                    </div>
                  )}
                </div>
              ) : (
                // For new expenses, show the wizard
                renderStepContent()
              )}
              
              {/* Confirmación de ajuste de fecha */}
              {dateAdjustment?.showConfirmation && (
                <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-md mb-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                        Ajuste Automático de Fecha
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                        <p>Se detectó que estás creando un gasto para el día 1 del mes ({dateAdjustment.originalDate}).</p>
                        <p className="mt-1">Para evitar problemas de filtrado, la fecha se ajustará automáticamente al día 2 ({dateAdjustment.adjustedDate}).</p>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          onClick={handleConfirmDateAdjustment}
                          disabled={isLoading}
                        >
                          {isLoading ? 'Guardando...' : 'Confirmar y Guardar'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleCancelDateAdjustment}
                          disabled={isLoading}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 mt-4">
                  {error}
                </div>
              )}
              
              <div className="mt-6 flex justify-between">
                {initialData ? (
                  // Buttons for edit mode
                  <div className="flex justify-end gap-2 w-full">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      disabled={isLoading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Guardando...' : 'Guardar Cambios'}
                    </Button>
                  </div>
                ) : (
                  // Buttons for wizard mode
                  <>
                    <div>
                      {currentStep > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={goToPreviousStep}
                          icon={<ArrowLeft className="h-4 w-4 mr-1" />}
                        >
                          Anterior
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        disabled={isLoading}
                      >
                        Cancelar
                      </Button>
                      
                      {currentStep < 3 ? (
                        <Button
                          type="button"
                          variant="primary"
                          onClick={goToNextStep}
                          disabled={!isCurrentStepValid()}
                          icon={<ArrowRight className="h-4 w-4 ml-1" />}
                          iconPosition="right"
                        >
                          Siguiente
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          onClick={handleSubmit}
                          disabled={isLoading || !isCurrentStepValid()}
                          icon={<CheckCircle className="h-4 w-4 ml-1" />}
                          iconPosition="right"
                        >
                          {isLoading ? 'Creando...' : 'Crear Gasto'}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </form>
            
            <Dialog.Close asChild>
              <button
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                aria-label="Close"
                disabled={isLoading}
              >
                <X className="h-6 w-6" />
              </button>
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};

export default ExpenseModal;